import { launcherStatesAllFalse, LauncherStatesData } from "../../hooks/stores/launcherStatesStore";
import { UIData } from "../../hooks/stores/uiStore";
import { stateViewsInfo } from "../../info/stateViewsInfo";

interface LauncherStateUtilsDependencies {
  UIData: UIData;
  setUIData: (data: Partial<UIData>) => void;
  launcherStatesData: LauncherStatesData;
  setLauncherStatesData: (data: Partial<LauncherStatesData>, replace?: boolean) => void;
}

export function createLauncherStateUtils({
  UIData,
  setUIData,
  launcherStatesData,
  setLauncherStatesData,
}: LauncherStateUtilsDependencies) {
  // Set launcher view and update states accordingly
  const setLauncherView = (view: keyof typeof stateViewsInfo) => {
    const viewInfo = stateViewsInfo[view];
    if (!viewInfo) return;

    // Create a new state object that starts with all states false
    const newStates = { ...launcherStatesAllFalse };

    // Apply only the states for the selected view
    Object.entries(viewInfo.launcherStates).forEach(([key, value]) => {
      if (key === 'failReason') {
        newStates.failReason = value as 'unknownError' | 'noDiskSpace' | 'noPermission' | 'noInternet';
      } else {
        newStates[key as keyof Omit<LauncherStatesData, 'failReason'>] = value as boolean;
      }
    });

    // Update all states at once
    setLauncherStatesData(newStates, true);

    // Update current view and launcher state in a single call
    setUIData({ 
      currentStateView: view,
      currentLauncherState: viewInfo.significantLauncherState 
    });
  };

  // Set launcher states and update view accordingly
  const setLauncherStates = (states: Partial<LauncherStatesData>, replace?: boolean) => {
    // Always merge with existing states unless explicitly told to replace
    const newStates = replace ? { ...launcherStatesAllFalse, ...states } : { ...launcherStatesData, ...states };
    setLauncherStatesData(newStates, replace);

    // Find the most appropriate view by checking all required states
    // Sort views by number of required states (most specific first)
    const view = Object.entries(stateViewsInfo)
      .sort(([_, a], [__, b]) => 
        Object.keys(b.launcherStates).length - Object.keys(a.launcherStates).length
      )
      .find(([_, info]) => {
        // Check if all required states for this view match
        return Object.entries(info.launcherStates).every(([key, value]) => {
          if (key === 'failReason') {
            return newStates.failReason === value;
          }
          return newStates[key as keyof Omit<LauncherStatesData, 'failReason'>] === value;
        });
      })?.[0] as keyof typeof stateViewsInfo;

    // Update current view and launcher state in a single call
    setUIData({ 
      currentStateView: view || null,
      currentLauncherState: view ? stateViewsInfo[view].significantLauncherState : [null, null]
    });
  };

  // Reset all states to false
  const resetLauncherStates = () => {
    setLauncherView('stateViewNotInstalled');
  };

  // Get current view based on states
  const getCurrentView = () => {
    return UIData.currentStateView;
  };

  // Get states for a specific view
  const getStatesForView = (view: keyof typeof stateViewsInfo) => {
    return stateViewsInfo[view]?.launcherStates || {};
  };

  // Check if a view is active based on current view in UIData
  const isViewActive = (view: keyof typeof stateViewsInfo) => {
    return UIData.currentStateView === view;
  };

  return {
    setLauncherStates,
    setLauncherView,
    resetLauncherStates,
    getCurrentView,
    getStatesForView,
    isViewActive,
  };
} 